import FJS3Config from '../config/FJS3Config';
import FJUtil from '../util/FJUtil';
import { getS3Client, getS3Upload, getS3ChecksumAlgorithm } from './FJModuleLoader';
import * as Sentry from '@sentry/react';
import { HeadObjectCommand } from '@aws-sdk/client-s3';
/**
 * @desc 上传到s3
 * @param bucketName
 * @param file
 * @param key
 * @param {{}} options
 * @param {{}} options.StorageClass - INTELLIGENT_TIERING: 保存成智能分层
 * @param progressCallback
 * @param abortRef - 中止上传的引用
 * @return {Promise<unknown>}
 * @private
 */
const _uploadToS3 = async (bucketName, file, key, options = {}, progressCallback = () => {}, abortRef) => {
    try {
        if (!file || !key) throw new Error('Missing key information');

        let contentType;
        if (!options.contentType) {
            contentType = 'application/octet-stream';
            let ext = FJUtil.getPathSuffix(key);
            if (ext === 'svg') contentType = 'image/svg+xml';
        } else {
            contentType = options.contentType;
        }

        const client = await getS3Client();
        const acl = !options.acl ? 'public-read' : options.acl;
        const Upload = await getS3Upload();
        const upload = new Upload({
            client,
            params: {
                ...options,
                Bucket: bucketName,
                Key: key,
                Body: file,
                ACL: acl,
                ContentType: contentType,
                ChecksumAlgorithm: getS3ChecksumAlgorithm().CRC32,
            },
        });

        upload.on('httpUploadProgress', progress => {
            if (progressCallback) {
                progressCallback({ type: 'progress', key: key, data: progress });
            }
        });

        if (abortRef) {
            abortRef.abort = () => {
                upload.abort();
            };
        }

        const data = await upload.done();

        return { type: 'done', key, data };
    } catch (e) {
        // 如果上传被中止，则不返回错误
        if (e.name !== 'AbortError') {
            return {
                type: 'error',
                msg: 'There was an error uploading : ' + e.message,
                key,
            };
        }
    }
};

const FJS3NetworkManager = {
    /**
     * s3上传
     * @param {file|blob} file
     * @param key
     * @param callback
     * @private
     */
    uploadToS3: (file, key, callback) => {
        if (!file || !key) return;
        if (key.indexOf('undefined') !== -1) {
            Sentry.captureMessage('uploadToS3 undefined:::' + key);
        }
        _uploadToS3(
            FJS3Config.ALBUM_BUCKET_NAME,
            file,
            key,
            {
                StorageClass: FJS3Config.STORAGE_TYPE.INTELLIGENT_TIERING,
                contentType: file.type,
            },
            callback,
        )
            .then(callback)
            .catch(callback);
    },
    batchUploadToS3(list) {
        return new Promise((resolve, reject) => {
            let promiseList = [];
            for (let i = 0; i < list.length; i++) {
                const item = list[i];
                const { file, key } = item;
                const promise = _uploadToS3(FJS3Config.ALBUM_BUCKET_NAME, file, key, {
                    StorageClass: FJS3Config.STORAGE_TYPE.INTELLIGENT_TIERING,
                    contentType: file.type,
                });
                promiseList.push(promise);
            }
            Promise.allSettled(promiseList).then(resolve).catch(reject);
        });
    },
    uploadFileAsync: async (file, key, options = {}) => {
        const { progress = () => {}, checkFileExists = true } = options;
        if (checkFileExists) {
            const exists = await FJS3NetworkManager.checkS3FileExists(key);
            if (exists) {
                progress(1);
                return true;
            }
        }
        return new Promise(resolve => {
            const progressCallback = response => {
                const { data = {} } = response;
                const { loaded = 0, total = 0 } = data;
                const p = loaded / total;
                progress?.(p);
            };
            FJS3NetworkManager.uploadToS3(file, key, response => {
                if (key !== response.key) return;
                switch (response.type) {
                    case 'progress':
                        progressCallback(response);
                        break;
                    case 'done':
                        progress(1);
                        resolve(true);
                        break;
                    case 'error':
                        resolve(false);
                        break;
                    default:
                }
            });
        });
    },
    /**
     * 检查s3文件是否存在
     * @param key
     * @returns {Promise<boolean>}
     * @private
     */
    checkS3FileExists: async key => {
        try {
            const client = await getS3Client();
            const command = new HeadObjectCommand({
                Bucket: FJS3Config.ALBUM_BUCKET_NAME,
                Key: key,
            });
            const result = await client.send(command);
            return !!result.ContentLength && result.ContentLength > 0;
        } catch (e) {
            return false;
        }
    },
};

export default FJS3NetworkManager;
